import { useMutation } from '@apollo/client';
import { useCallback, useEffect } from 'react';

import { SendWebConsentDataDocument } from './send-web-consent-mutation.gql-types';
import type {
  ServiceConsent,
  TCData,
  TCFVendor,
  UCUIEventDetail,
} from './usercentrics.types';

const TCF_STRING_READY_EVENT_STATUSES = new Set([
  'tcloaded',
  'useractioncomplete',
]);

const isUsercentricsAvailable = (): boolean | undefined => {
  return (
    typeof window !== 'undefined' &&
    window.__ucCmp &&
    typeof window.__ucCmp.getConsentDetails === 'function'
  );
};

const getTcfString = (): Promise<string | undefined> => {
  return new Promise((resolve) => {
    const tcfApi = window.__tcfapi;

    if (typeof tcfApi !== 'function') {
      resolve(undefined);
      return;
    }

    let resolved = false;

    const resolveOnce = (
      tcfString: string | undefined,
      listenerId?: number
    ): void => {
      if (resolved) {
        return;
      }

      resolved = true;
      window.clearTimeout(timeout);

      if (listenerId !== undefined) {
        tcfApi('removeEventListener', 2, () => undefined, listenerId);
      }

      resolve(tcfString);
    };

    const timeout = window.setTimeout(() => {
      resolveOnce(undefined);
    }, 1000);

    tcfApi('addEventListener', 2, (tcData: TCData, success: boolean) => {
      if (!success) {
        resolveOnce(undefined, tcData.listenerId);
        return;
      }

      if (TCF_STRING_READY_EVENT_STATUSES.has(tcData.eventStatus ?? '')) {
        resolveOnce(tcData.tcString, tcData.listenerId);
      }
    });
  });
};

// Once a user clicks on the "Accept all" or "Deny all" button in the Usercentrics UI,
// the consent data is sent to the backend.
export const useUsercentricsEvent = (): void => {
  const [sendWebConsent] = useMutation(SendWebConsentDataDocument);

  const handleUCUICMPEvent = useCallback(
    (event: CustomEvent<UCUIEventDetail>): void => {
      if (!isUsercentricsAvailable()) {
        return;
      }

      const type = event?.detail?.type;

      const sendConsentData = async (): Promise<void> => {
        try {
          const [consentDetails, tcfString] = await Promise.all([
            window.__ucCmp!.getConsentDetails(),
            getTcfString(),
          ]);

          const services = (
            Object.entries(consentDetails.services) as [
              string,
              ServiceConsent,
            ][]
          ).map(([id, service]) => ({
            id,
            name: service.name,
            consent: {
              status: service.consent.given ? 'ACCEPTED' : 'DENIED',
            },
          }));

          const tcfVendors = window.__ucCmp!.cmpController?.tcf.vendors ?? {};

          const payload = {
            channel: 'web',
            domain: window.location.origin,
            controllerId: consentDetails.consent.controllerId,
            nonTcfVendors: JSON.stringify(services),
            ...(tcfString ? { tcfString } : {}),
            tcfVendors: JSON.stringify(
              (Object.values(tcfVendors) as TCFVendor[]).map(
                ({ id, name, consent }) => ({
                  id,
                  name,
                  consent,
                })
              )
            ),
          };

          console.log('sending web consent data');
          sendWebConsent({ variables: { input: payload } });
        } catch (error) {
          console.error('Failed to get consent details:', error);
        }
      };

      switch (type) {
        case 'SAVE':
        case 'DENY_ALL':
        case 'ACCEPT_ALL':
          void sendConsentData();
          break;
        default:
          break;
      }
    },
    [sendWebConsent]
  );

  useEffect(() => {
    window.addEventListener(
      'UC_UI_CMP_EVENT',
      handleUCUICMPEvent as EventListener
    );

    return () => {
      window.removeEventListener(
        'UC_UI_CMP_EVENT',
        handleUCUICMPEvent as EventListener
      );
    };
  }, [handleUCUICMPEvent]);
};
